import { useState } from "react";
import Dataset from "datasets/types/interfaces/dataset";
import { Button } from "~/components/ui/button";
import Combobox from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupIndicator } from "~/components/ui/radio-group";
import useToast from "~/components/ui/toast";
import { useAgentList } from "~/hooks/useAgentList";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { AgentListView, AgentStatus } from "~/services/agents";
import { catalogDatasetsListUrl } from "~/services/catalog";
import { createQuickAction, QuickActionPlacement } from "~/services/quickActions";
import { IconPicker } from "./IconPicker";
import { DEFAULT_ICON, PLACEMENT_ICON, PLACEMENT_LABEL } from "./constants";

interface CreateQuickActionModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => unknown;
}

const PLACEMENT_ORDER: QuickActionPlacement[] = [
  QuickActionPlacement.HOME,
  QuickActionPlacement.AGENT,
  QuickActionPlacement.DATASET,
];

const emptyState = {
  icon: DEFAULT_ICON as string,
  title: "",
  subtitle: "",
  placement: QuickActionPlacement.HOME as QuickActionPlacement,
  placementId: "",
};

export const CreateQuickActionModal = ({
  open,
  onClose,
  onCreated,
}: CreateQuickActionModalProps) => {
  const { showSuccess, showError } = useToast();
  const [form, setForm] = useState(emptyState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: agentList } = useAgentList(
    open && form.placement === QuickActionPlacement.AGENT,
    AgentListView.USABLE
  );
  const enabledAgents = (agentList?.items ?? []).filter(
    (agent) => agent.status !== AgentStatus.DISABLED
  );
  const { data: datasets } = useFetcher<Dataset[]>(
    open && form.placement === QuickActionPlacement.DATASET ? catalogDatasetsListUrl : null
  );

  const hasRequiredTarget = form.placement === QuickActionPlacement.HOME || !!form.placementId;
  const canSubmit = !!form.title.trim() && hasRequiredTarget;

  const handleClose = () => {
    setForm(emptyState);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    const title = form.title.trim();
    setIsSubmitting(true);
    try {
      await createQuickAction({
        placement: form.placement,
        placement_id: form.placement === QuickActionPlacement.HOME ? undefined : form.placementId,
        title,
        subtitle: form.subtitle.trim() || null,
        icon: form.icon,
        enabled: false,
        project_name: title,
        agent_ids: form.placement === QuickActionPlacement.AGENT ? [form.placementId] : undefined,
        dataset_ids:
          form.placement === QuickActionPlacement.DATASET ? [form.placementId] : undefined,
      });
      showSuccess("Quick action created");
      await onCreated();
      handleClose();
    } catch {
      showError("Failed to create quick action, please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New Quick Action</DialogTitle>
        </DialogHeader>

        <div className="flex min-w-0 flex-col gap-4">
          <div className="space-y-2">
            <Label>Placement</Label>
            <RadioGroup
              value={form.placement}
              onValueChange={(value) =>
                setForm((s) => ({
                  ...s,
                  placement: value as QuickActionPlacement,
                  placementId: "",
                }))
              }
              className="flex gap-4"
            >
              {PLACEMENT_ORDER.map((placement) => {
                const PlacementIcon = PLACEMENT_ICON[placement];
                const isSelected = form.placement === placement;
                return (
                  <label
                    key={placement}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                      isSelected
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-input text-muted-foreground hover:bg-muted/50"
                    )}
                  >
                    <RadioGroupIndicator value={placement} className="sr-only" />
                    <PlacementIcon className="size-4" />
                    {PLACEMENT_LABEL[placement]}
                  </label>
                );
              })}
            </RadioGroup>

            {form.placement === QuickActionPlacement.AGENT && (
              <Combobox
                value={form.placementId || null}
                onChange={(value) => setForm((s) => ({ ...s, placementId: value }))}
                placeholder="Choose an agent"
                searchPlaceholder="Search agents..."
                options={enabledAgents.map((agent) => ({
                  value: agent.id,
                  label: agent.display_name,
                  icon: <AgentAvatar agent={agent} size={16} />,
                }))}
              />
            )}
            {form.placement === QuickActionPlacement.DATASET && (
              <Combobox
                value={form.placementId || null}
                onChange={(value) => setForm((s) => ({ ...s, placementId: value }))}
                placeholder="Choose a dataset"
                searchPlaceholder="Search datasets..."
                options={(datasets ?? []).map((dataset) => ({
                  value: dataset.id,
                  label: dataset.title,
                  icon: <DatasetIcon dataset={dataset} size={16} />,
                }))}
              />
            )}
          </div>

          <div className="flex gap-4">
            <div className="space-y-1.5">
              <Label>Icon</Label>
              <IconPicker value={form.icon} onChange={(icon) => setForm((s) => ({ ...s, icon }))} />
            </div>
            <div className="flex-1 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="qa-title">Title</Label>
                <Input
                  id="qa-title"
                  value={form.title}
                  onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                  placeholder="Name this quick action"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="qa-subtitle">Subtitle</Label>
                <Input
                  id="qa-subtitle"
                  value={form.subtitle}
                  onChange={(e) => setForm((s) => ({ ...s, subtitle: e.target.value }))}
                  placeholder="Short supporting description"
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            A quick action will automatically create a project - configure its details from the
            sidebar afterward.
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Creating…" : "Create Quick Action"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
